import { SubscriptionInterval, SubscriptionPlanListItemDto } from '../api';

export interface SubscriptionPlanComparison {
  monthlyTotal: number;
  annualTotal: number;
  annualEquivalentMonthlyTotal: number;
  annualReferenceTotal: number;
  annualSavings: number;
  annualDiscountPercentage: number;
  monthlyMonths: number;
  annualMonths: number;
  hasComparableSavings: boolean;
}

export interface SubscriptionPlanTaxBreakdown {
  subtotal: number;
  taxAmount: number;
  total: number;
  taxRate: number;
  hasTax: boolean;
}

export const normalizeSubscriptionPlanCurrency = (currency?: string) => (currency && currency.length === 3 ? currency : 'EUR');

export const getSubscriptionPlanGrossTotal = (plan?: SubscriptionPlanListItemDto) => plan?.priceIncludingTax ?? 0;

export const buildSubscriptionPlanTaxBreakdown = (plan?: SubscriptionPlanListItemDto): SubscriptionPlanTaxBreakdown => {
  const total = getSubscriptionPlanGrossTotal(plan);
  const subtotal = plan?.priceExcludingTax ?? total;
  const taxAmount = Math.max(total - subtotal, 0);
  const taxRate = plan?.taxRate ?? 0;
  const hasTax = taxAmount > 0;

  return {
    subtotal,
    taxAmount,
    total,
    taxRate,
    hasTax,
  };
};

export const getSubscriptionPlanDurationInMonths = (plan?: SubscriptionPlanListItemDto) => {
  if (!plan?.intervalCount || plan.intervalCount <= 0) {
    return 0;
  }

  switch (plan.interval) {
    case SubscriptionInterval.Years:
      return plan.intervalCount * 12;
    case SubscriptionInterval.Months:
      return plan.intervalCount;
    case SubscriptionInterval.Days:
      return plan.intervalCount / 30;
    default:
      return 0;
  }
};

export const buildSubscriptionPlanComparison = (
  monthlyPlan?: SubscriptionPlanListItemDto,
  annualPlan?: SubscriptionPlanListItemDto,
): SubscriptionPlanComparison => {
  const monthlyTotal = getSubscriptionPlanGrossTotal(monthlyPlan);
  const annualTotal = getSubscriptionPlanGrossTotal(annualPlan);
  const monthlyMonths = getSubscriptionPlanDurationInMonths(monthlyPlan);
  const annualMonths = getSubscriptionPlanDurationInMonths(annualPlan);
  const annualEquivalentMonthlyTotal = annualMonths > 0 ? annualTotal / annualMonths : annualTotal;
  const annualReferenceTotal =
    monthlyMonths > 0 && annualMonths > 0 ? (monthlyTotal / monthlyMonths) * annualMonths : 0;
  const hasComparableSavings =
    normalizeSubscriptionPlanCurrency(monthlyPlan?.currency) === normalizeSubscriptionPlanCurrency(annualPlan?.currency) &&
    annualReferenceTotal > annualTotal;
  const annualSavings = hasComparableSavings ? annualReferenceTotal - annualTotal : 0;
  const annualDiscountPercentage =
    annualReferenceTotal > 0 && annualSavings > 0 ? Math.round((annualSavings / annualReferenceTotal) * 100) : 0;

  return {
    monthlyTotal,
    annualTotal,
    annualEquivalentMonthlyTotal,
    annualReferenceTotal,
    annualSavings,
    annualDiscountPercentage,
    monthlyMonths,
    annualMonths,
    hasComparableSavings,
  };
};
